const DEBUGGING: boolean = process.env.NODE_ENV === 'development'

import { BaseObject, MongoObject } from './_baseClasses'
import { Challenge } from '@/Challenge'
import * as utils_topshot from './utils_topshot'
import * as utils_time from './utils_time'
import { Plotly } from '@rleys/vue-plotly-basic'
import { Subedition } from '@/Subedition'

export interface AdvancedSearch {
  league?: string,
  team?: string,
  playCategory?: string,
  minPrice?: number,
  maxPrice?: number,
  minFMV?: number,
  maxFMV?: number,
  minSerial?: number,
  maxSerial?: number,
  minDiscount?: number,
  jersey?: boolean,
  lastMint?: boolean,
  neverSold?: boolean,
  locked?: boolean,
  notlocked?: boolean,
  tsdebut?: boolean,
  rookieYear?: boolean,
  rookieMint?: boolean,
  rookiePremiere?: boolean
  champYear?: boolean
  challengeReward?: boolean,
  craftingChallengeReward?: boolean,
  rarity?: string,
  series?: number,
}

// for passing to client to graph median sale prices (cool!)
export interface RefValuesForEdition {
  days: number[]
  values: number[]
  sampleSizes: number[]
}

export class Moment extends BaseObject {
  protected static ALLOW_UNEXPECTED_FIELDS: boolean = true

  public momentID: number = null
  public setID: number = null
  public playID: number = null
  public subeditionID: number = null
  public serialNumber: number = null             // e.g. 5178
  public momentGUID?: string = null
  public setGUID: string = null
  public playGUID: string = null
  public sellerUsername: string = null           // might help with deals linking to updated moment page

  public imageURL: string = null                 // e.g. https://assets.nbatopshot.com/editions/2_base_set_common/247d68b1-e71e-4e0f-be5e-d70193bec510/play_247d68b1-e71e-4e0f-be5e-d70193bec510_2_base_set_common_capture_Hero_2880_2880_Black.jpg
  public name: string = null                     // e.g. Coby White
  public playerID: number = null
  public dateOfMoment: Date = null               // e.g. Jan 1 2021
  public playCategory: string = null             // e.g. 3 Pointer
  public team: string = null                     // e.g. Atlanta Hawks -> ATL on August 15, 2021
  public setName: string = null                  // e.g. Base Set
  public series: number = null                   // e.g. 0,2,3
  public setVisualId: string = null              // e.g. SET_VISUAL_RARE

  public circulationCount: number = null
  public flowRetired: boolean = null

  public subeditionName: string = null

  // badges
  public isFirstMoment: boolean = null
  public isRookieMoment: boolean = null
  public isRookieMint: boolean = null
  public isRookieDebut: boolean = null
  public isChampionshipYear: boolean = null
  public isChallengeReward: boolean = null
  public isCraftingChallengeReward: boolean = null

  // specials
  public isJerseyNumber: boolean = null
  public isMaxSerial: boolean = null
  public numTimesSold: number = null

  // really dynamic
  public valueFMV: number = null
  public valueFMV2?: number = null
  public numSales: number = null
  public liquidityRating: number = null

  // just for processing, not for client
  private mintedMoment: any = null

  // active challenge acronyms that apply
  public challenges: string[] = null
  public expiredChallenges: string[] = null

  // for graphing
  public refValuesForEdition: RefValuesForEdition = null
  public lastDayMovement: number = null

  // complex, class-based fields (require to be hydrated after loading from external sources like mongo or elastic)

  constructor(po?: object) {
    super()
    if (po) this.injectPlainObject(po)

    // for temporary backwards compatibility with old deals that didn't have server-computed liquidityRating
    if (!this.liquidityRating) this.liquidityRating = Math.round(Math.pow(this.numSales, 0.40) - 1)
    if (this.liquidityRating > 5) this.liquidityRating = 5

    this.hydrate()
  }

  protected hydrate(): void {
    super.hydrate()
    if (typeof this.dateOfMoment === 'string') this.dateOfMoment = new Date(this.dateOfMoment)

    // debugging
    // this.subeditionID = 1
    // this.subeditionName = 'diamond'
  }

  public isSafe(): boolean {
    if (this.mintedMoment) return false
    return true
  }

  // Oct 28, 2022 - use new cool way, ignoring what i scraped into duo
  public getImageURLForStyle(width: number): string {
    // new way
    return `background-image:url('https://assets.nbatopshot.com/media/${this.momentID}?width=${width}')`

    // old way
    if (!this.imageURL) return ''
    return `background-image:url('${this.imageURL.replace('com/editions/', 'com/resize/editions/')}?width=${width}')`
  }

  // these hold only for now.  they keep changing stuff, so the right way to do this is to 
  //  just do it by a hardcode using the setID.
  // https://blog.nbatopshot.com/posts/edition-sizes-series-2 -- see, it's kind of arbitrary
  public isCommon(): boolean {
    if (this.setVisualId) return this.setVisualId === 'SET_VISUAL_COMMON'
    return this.circulationCount > 2021
  }
  public isFandom(): boolean {
    return this.setVisualId === 'SET_VISUAL_FANDOM'
  }
  public isRare(): boolean {
    if (this.setVisualId) return this.setVisualId === 'SET_VISUAL_RARE'
    return !this.isCommon() && this.circulationCount > 100
  }
  public isLegendary(): boolean {
    if (this.setVisualId) return this.setVisualId === 'SET_VISUAL_LEGENDARY'
    return !this.isCommon() && !this.isRare()
  }
  public isUltimate(): boolean {
    if (this.setVisualId) return this.setVisualId === 'SET_VISUAL_ULTIMATE'
    return !this.isCommon() && !this.isRare() && !this.isLegendary()
  }  

  public getClassNameBasedOnRarity(): string {
    if (this.isCommon() && !this.flowRetired) return 'common'
    if (this.isCommon() && this.flowRetired) return 'limitedEdition'
    if (this.isFandom()) return 'fandom'
    if (this.isRare()) return 'rare'
    if (this.isLegendary()) return 'legendary'
    if (this.isUltimate()) return 'ultimate'
  }

  public getFirstMomentText(): string {
    return `This is ${this.name}'s Top Shot debut.`
  }
  public getRookieMomentText(): string {
    return `This moment is from ${this.name}'s rookie season.`
  }
  public getRookieMintText(): string {
    return `This moment is from ${this.name}'s rookie season and was also minted during their rookie season.`
  }
  public getRookieDebutText(): string {
    return `This moment is from ${this.name}'s rookie premiere game in the NBA.`
  }
  public getChampYearText(): string {
    return `This moment is from ${this.name}'s championship year in the NBA.`
  }
  public getChallengeRewardText(): string {
    return `This moment was minted and issued as a challenge reward.`
  }
  public getCraftingChallengeRewardText(): string {
    return `This moment was minted and issued as a crafting challenge reward.`
  }

  public getTopShotLink(favorMomentURL: boolean): string {
    // if (favorMomentURL && this.momentGUID && this.sellerUsername) return `https://nbatopshot.com/moment/${this.sellerUsername}+${this.momentGUID}`   -- sometimes get Airball screen
    if (favorMomentURL) return `https://nbatopshot.com/moment/${this.momentID}`   // Feb 21, 2024 -- // July 26, 2026 - moved this to boe before momentGUID because momentGUID's stopped working on nbatopshot.com.
    if (favorMomentURL && this.momentGUID) return `https://nbatopshot.com/moment/${this.momentGUID}`
    if (!this.setGUID || !this.playGUID) return null
    return `https://nbatopshot.com/listings/p2p/${this.setGUID}+${this.playGUID}`
  }

  public getLiveTokenSearchLink(): string {
    return `/listings/topshot?setID=${this.setID}&playID=${this.playID}`
  }

  public getChallengeTitle(acronym: string): string {
    const challenge: Challenge = this.getChallengeFromAcronym(acronym)
    if (!challenge) return ''
    return challenge.userChallenge.name
  }

  public getChallengeText(acronym: string, expired: boolean): string {
    const challenge: Challenge = this.getChallengeFromAcronym(acronym)
    if (!challenge) return ''
    const timeDiff: number = challenge.userChallenge.expirationDate.getTime() - new Date().getTime()
    const numUsersCompleted: number = challenge.userChallenge.numUsersCompleted || challenge.lastNumCompleted || 0

    if (!expired)
      return `This ${this.name} moment is required to complete the ${challenge.userChallenge.name} challenge ending in ${utils_time.shortTimeDiff(timeDiff)}. Thus far, ${numUsersCompleted.toLocaleString()} have completed it.`
    else
      return `This ${this.name} moment was required to complete the ${challenge.userChallenge.name} challenge that ended ${utils_time.shortTimeDiff(-timeDiff)} ago. By the end, ${numUsersCompleted.toLocaleString()} had completed it.`
  }

  public getChallengeFromAcronym(acronym: string): Challenge {
    const challenges: Challenge[] = utils_topshot.getChallenges()
    if (challenges) for (const challenge of challenges) if (challenge.acronym === acronym) return challenge
    const expiredChallenges: Challenge[] = utils_topshot.getExpiredChallenges()
    if (expiredChallenges) for (const challenge of expiredChallenges) if (challenge.acronym === acronym) return challenge
  }

  public getAbbreviatedSetName(): string {
    let abbreviatedName: string
    abbreviatedName = this.setName.replace('Conference', 'Conf')
    abbreviatedName = abbreviatedName.replace('Hometown Showdown', 'Home SD')
    abbreviatedName = abbreviatedName.replace('And Then There Were Four', 'Then There Were 4')
    abbreviatedName = abbreviatedName.replace('Run It Back: Legacies 1986-87', 'RIB: Legacies 1986-87')
    abbreviatedName = abbreviatedName.replace('WNBA Game Recognize Game', 'WNBA GRG')
    abbreviatedName = abbreviatedName.replace('The Anthology: Magic Johnson', 'The Anthology: Magic J')

    // Feb 26, 2024: ok not really an abbreviation, but a fix for stupid Dapper mistake
    abbreviatedName = abbreviatedName.replace('Base Set6', 'Base Set')
    abbreviatedName = abbreviatedName.replace('Rookie Debut6', 'Rookie Debut')
    abbreviatedName = abbreviatedName.replace('Throwdowns6', 'Throwdowns')

    // May 8, 2024
    abbreviatedName = abbreviatedName.replace("The Champion's Path 2024", "Champion's Path 2024")

    return abbreviatedName
  }

  // https://plotly.com/javascript/line-charts/
  public getPlotlyData() {
    if (!this.refValuesForEdition) return null
    const x: number[] = []
    const y: number[] = []
    for (const [i, day] of this.refValuesForEdition.days.entries()) {
      x.push(day)
      y.push(this.refValuesForEdition.values[i])
    }
    const trace = { x, y, type: 'scatter', mode: 'lines', line: { shape: 'spline' } }
    return [ trace ]
  }
  public getPlotyLayout() {
    return {
      width: 260, height: 150,
      margin: { l: 32, r: 7, t: 0, b: 25 },
      linecolor: "#444",
      gridcolor: "#eee",
      zerolinecolor: "#eee",
      yaxis: { 
        rangemode: 'tozero',
        tickprefix: '$',
        tickfont: { size: 10 }
      },
      xaxis: { 
        rangemode: 'tozero',
        ticksuffix: 'd',
        tickfont: { size: 10 },
        title: { text: '(Median sale prices for all serials)', font: { size: 10, color: "#C88" }}
      },
      hoverlabel: {
        font: { size: 10 }
      }      
    }
  }
  public getOldestDay(): number {
    if (!this.refValuesForEdition) return null
    if (this.refValuesForEdition.days.length === 0) return null
    return this.refValuesForEdition.days[this.refValuesForEdition.days.length - 1]
  }

  public getPlotlyTitle(): string {
    let title: string = `Trend last ${-this.getOldestDay()} days`
    if (this.lastDayMovement > 0.005) title += ` (+${Math.round(this.lastDayMovement * 100)}% in 24h)`
    if (this.lastDayMovement < -0.005) title += ` (${Math.round(this.lastDayMovement * 100)}% in 24h)`
    return title
  }

  public getTeamAbbreviation(): string {
    return utils_topshot.teamAbbreviation(this.team)
  }

  public getNiceDateOfMoment(): string {
    if (!this.dateOfMoment) return ''       // because of Fit Check reel moments that don't have a date
    const d: Date = this.dateOfMoment
    if (d.getTime() === 0) return ''        // because of Fit Check reel moments that don't have a date
    const ye = new Intl.DateTimeFormat('en', { year: '2-digit' }).format(d);
    const mo = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(d);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d);
    return `${mo}-${da}-${ye}`
  }

  public getFlashAuctionStartingBid(): number {
    let startingBid: number = Number((Math.round(this.valueFMV) / 2).toPrecision(3))
    if (startingBid === 0) startingBid = 0.50
    return startingBid
  }
  public getFlashAuctionStartingBidAsNiceString(): string {
    const bid: number = this.getFlashAuctionStartingBid()
    if (bid === Math.floor(bid)) return bid.toString()
    return bid.toFixed(2)
  }

  public getSeriesName(): string {
    switch (this.series) {
      case 0: return 'Series 1'
      case 1: return 'Series 1'
      case 2: return 'Series 2'
      case 3: return 'Summer 2021'
      case 4: return 'Series 3'
      case 5: return 'Series 4'
      case 6: return 'Series 2023-24'
      case 7: return 'Series 2024-25'
      case 8: return 'Series 2025-26'
    }
  }

  public getSeriesAbbreviation(): string {
    switch (this.series) {
      case 0: return 'S1'
      case 1: return 'S1'
      case 2: return 'S2'
      case 3: return 'Sum 21'
      case 4: return 'S3'
      case 5: return 'S4'
      case 6: return '23-24'
      case 7: return '24-25'
      case 8: return '25-26'
    }
  }  

  public getSubeditionName(): string {
    if (this.subeditionID && this.subeditionName) return this.subeditionName
    return ''
  }

  public getSubeditionShortName(): string {
    if (this.subeditionID && this.subeditionName) return Subedition.getShortName(this.subeditionName)
    return ''
  }
  

}



(async () => {
})();
